"use client";

import { useCallback, useSyncExternalStore } from "react";
import { openAuthPrompt } from "@/lib/authPrompt";

// Favoris produit RATTACHÉS AU COMPTE (Better Auth), plus au navigateur.
//
// - Connecté : la liste vient de /api/favorites, chaque ★ écrit en base → on
//   retrouve ses favoris sur tous ses appareils.
// - Non connecté : on n'enregistre rien, on RETIENT le produit visé (voir plus bas)
//   et on ouvre la modale de connexion. Le favori est posé au retour.
//
// Les boutons (FavoriteButton, FavoriteStamp) n'ont rien à savoir de tout ça :
// l'interface (`useFavorite`, `toggleFavorite`, `onFavoriteChange`) est inchangée.
// Un seul magasin partagé pour toute la page (le même produit peut apparaître
// dans « Meilleures affaires » ET dans la grille → cliquer l'un allume l'autre).

let ids = new Set<string>();
let loggedIn = false;
let loaded = false;
// Chargement EN COURS. Sans ce drapeau, deux rendus rapprochés de <FavoritesSync/>
// lancent deux GET : le second écrase l'état du premier — y compris un favori
// tout juste posé, qui s'éteint alors sous les yeux de l'utilisateur.
let loading = false;
const listeners = new Set<() => void>();

function notify() {
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// --- Favori EN ATTENTE : le geste survit à la connexion ----------------------
// Cliquer ♥ sans compte, c'est déjà avoir choisi. On retient donc le produit visé,
// et on le pose dès que la session existe — sinon il faut re-trouver la fiche et
// re-cliquer, et l'intention se perd en route.
//
// `localStorage` et pas `sessionStorage` : la CONFIRMATION D'ADRESSE se fait en
// cliquant un lien reçu par mail, qui peut ouvrir un AUTRE onglet — sessionStorage
// n'y survivrait pas. D'où aussi la péremption : une intention vieille d'une heure
// n'en est plus une, et personne ne doit voir un favori apparaître par surprise.
const PENDING_KEY = "atlanx:favori-en-attente";
const PENDING_TTL_MS = 60 * 60 * 1000;

function rememberPending(id: string) {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({ id, at: Date.now() }));
  } catch {
    /* stockage indisponible : on perd juste le rattrapage, pas le reste */
  }
}

/** Abandon de la connexion (modale fermée) : l'intention ne doit pas traîner. */
export function clearPendingFavorite() {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* rien à oublier */
  }
}

/** Lit ET consomme l'intention : elle ne doit jamais s'appliquer deux fois. */
function takePending(): string | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    localStorage.removeItem(PENDING_KEY);
    const p = JSON.parse(raw) as { id?: unknown; at?: unknown };
    if (typeof p.id !== "string" || typeof p.at !== "number") return null;
    return Date.now() - p.at > PENDING_TTL_MS ? null : p.id;
  } catch {
    return null;
  }
}

/**
 * Pose le favori mis en attente avant la connexion.
 * ⚠️ À n'appeler QU'APRÈS le chargement des favoris du compte : `toggleFavorite`
 * bascule (il n'ajoute pas), donc sur une liste pas encore chargée il RETIRERAIT
 * un favori que la personne possédait déjà sur un autre appareil.
 */
function applyPending() {
  const id = takePending();
  if (id && !ids.has(id)) toggleFavorite(id);
}

// --- Synchronisation avec l'état de session (appelé par <FavoritesSync/>) -----
// Monté une seule fois au layout racine, ce composant nous dit si l'utilisateur
// est connecté ; on charge alors ses favoris une fois, ou on vide à la déconnexion.
export function setFavoritesAuth(isLoggedIn: boolean) {
  // « Déjà à jour OU déjà en train de se mettre à jour » → on ne relance rien.
  if (isLoggedIn === loggedIn && (loaded || loading)) return;
  loggedIn = isLoggedIn;
  if (!isLoggedIn) {
    ids = new Set();
    loaded = true;
    loading = false;
    notify();
    return;
  }
  loading = true;
  loaded = false;
  fetch("/api/favorites")
    .then((r) => (r.ok ? r.json() : { ids: [] }))
    .then((d: { ids?: string[] }) => {
      ids = new Set(Array.isArray(d.ids) ? d.ids : []);
      loaded = true;
      loading = false;
      notify();
      // La session vient d'exister : c'est ICI qu'on honore le ♥ cliqué avant de
      // se connecter (et nulle part ailleurs — un seul point de rattrapage).
      applyPending();
    })
    .catch(() => {
      loaded = true;
      loading = false;
    });
}

// --- Annonce du geste (canal séparé de l'état, pour le bandeau de confirmation) ---
// `error` : le serveur a REFUSÉ (plafond de favoris atteint). L'état optimiste est alors
// annulé et le message part par ce même canal — le tampon de la carte l'affiche à la place
// du « Ajouté aux favoris », donc là où l'œil vient de cliquer.
export type FavoriteEvent = { id: string; added: boolean; at: number; error?: string };

const announcers = new Set<(e: FavoriteEvent) => void>();

export function onFavoriteChange(fn: (e: FavoriteEvent) => void) {
  announcers.add(fn);
  return () => {
    announcers.delete(fn);
  };
}

export function toggleFavorite(id: string) {
  // Pas de compte → on retient le produit visé, puis on invite à se connecter.
  // Le favori sera posé tout seul au retour (cf. `applyPending`).
  if (!loggedIn) {
    rememberPending(id);
    openAuthPrompt("favorite");
    return;
  }
  const added = !ids.has(id);
  // Mise à jour optimiste (le ★ répond tout de suite ; l'API suit).
  const next = new Set(ids);
  if (added) next.add(id);
  else next.delete(id);
  ids = next;
  notify();
  for (const fn of announcers) fn({ id, added, at: Date.now() });

  // Annule la maj optimiste pour rester cohérent avec la base.
  const rollback = () => {
    const back = new Set(ids);
    if (added) back.delete(id);
    else back.add(id);
    ids = back;
    notify();
  };

  fetch("/api/favorites", {
    method: added ? "POST" : "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ productId: id }),
  })
    .then(async (res) => {
      // Le serveur REFUSE (plafond atteint) : sans ce cas, le ★ restait allumé alors que
      // rien n'était enregistré — l'utilisateur croyait son favori posé.
      if (res.ok) return;
      const d = (await res.json().catch(() => null)) as { error?: string } | null;
      rollback();
      const message = d?.error ?? "Impossible d'enregistrer ce favori.";
      for (const fn of announcers) fn({ id, added: false, at: Date.now(), error: message });
    })
    .catch(rollback); // réseau KO
}

/**
 * `isFav` d'un produit, synchronisé entre tous les boutons de la page.
 * Le rendu SERVEUR répond toujours « non » (l'état favoris n'existe que côté
 * client, après chargement) — React réconcilie à l'hydratation.
 */
export function useFavorite(id: string) {
  const isFav = useSyncExternalStore(
    subscribe,
    () => ids.has(id),
    () => false,
  );
  const toggle = useCallback(() => toggleFavorite(id), [id]);
  return { isFav, toggle };
}
